import { useContext, useRef, useState } from "react";
import { NotificationsContext } from "@ui-components/Notifications";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import useAsyncError from "@hooks/useAsyncError";
import { useIntl } from "react-intl";
import CountryConfigContext from "../../../CountryConfigContext";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";
import useGetSurveyResponses from "@hooks/useGetSurveyResponses";
import type { SurveyOutputsType } from "@feature/surveys/types";

import { getSurveyOutputs } from "../lib/utils";

import { ServiceSurveysWithIdSchema } from "../../types";

import {
  mapQuestionLogics,
  getGenerateQuotationPayload,
} from "@feature/surveys/lib/design-quotation/prepopulation-logic";

export const useGenerateQuotationSurvey = () => {
  // context
  const { push } = useContext(NotificationsContext);

  const {
    config: { services, products, systems },
  } = useContext(CountryConfigContext);
  // hooks
  const intl = useIntl();
  const countryApi = useCountryAxiosInstance();
  const throwError = useAsyncError();
  const getServiceConfiguration = useGetServiceConfiguration(false);
  const getSurveyResponses = useGetSurveyResponses();
  const getConnectionResults = (surveyId: number) => countryApi.get(`/connectors/${surveyId}`)

  const responsecache = useRef(
    {} as { [surveyId: number]: Promise<any> | undefined }
  );
  const configurationcache = useRef(
    {} as { [serviceId: number]: { [version:number]:Promise<any> | undefined }}
  );
  const connectionResultsCache = useRef(
    {} as { [surveyId: number]: Promise<any> | undefined }
  );

  const cachedGetSurveyResponses = (surveyId: number) => {
    if (responsecache.current[surveyId]) {
      return responsecache.current[surveyId];
    }
    const promise = getSurveyResponses(surveyId);
    responsecache.current[surveyId] = promise;
    return promise;
  };

  const cachedGetServiceConf = (serviceId: number, version:number) => {

    if (configurationcache.current?.[serviceId]?.[version]) {
      return configurationcache.current[serviceId][version];
    }
    const promise = getServiceConfiguration(serviceId, version);
    configurationcache.current[serviceId] = promise;
    return promise;
  };

  const cachedGetConnectionResults = (surveyId: number) => {
    if (connectionResultsCache.current[surveyId]) {
      return connectionResultsCache.current[surveyId];
    }
    const promise = getConnectionResults(surveyId);
    connectionResultsCache.current[surveyId] = promise;
    return promise;
  }

  // state
  const [loading, setLoading] = useState(false);

  // handlers
  const dispatch = async (
    designSurveys: ServiceSurveysWithIdSchema[],
    quotationServiceId: number,
    version: number,
    name: string | null,
  ) => {
    setLoading(true);
    try {
      const destinationServiceConfiguration = await getServiceConfiguration(
        quotationServiceId,
        version
      );
      const blockRuleMapping = await mapQuestionLogics(
        // @ts-ignore
        destinationServiceConfiguration,
        designSurveys,
        cachedGetSurveyResponses,
        cachedGetServiceConf,
        cachedGetConnectionResults
      );
      const designSurveyValuesP = await Promise.all(
        designSurveys.map(async (ds) =>
          getSurveyOutputs(
            countryApi!,
            ds,
            services,
            products,
            systems,
            "admin", // always shown to everyone
            getServiceConfiguration,
            true
          )
        )
      );
      const designSurveyValues = designSurveys.reduce(
        (acc, { id }, idx) => ({ ...acc, [id]: designSurveyValuesP[idx] }),
        {} as Record<number, SurveyOutputsType>
      );

      const payload = {
        name,
        ...(getGenerateQuotationPayload(
          blockRuleMapping,
          designSurveys,
          designSurveyValues,
        ))
      };
      return (await countryApi!.post(
        `/service_surveys/generate_quotation/${quotationServiceId}`,
        payload
      )) as ServiceSurveysWithIdSchema;
    } catch (error) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "server_error" }),
        message: "GENERATE_QUOTATION_FROM_DESIGN",
      });
      throwError(error);
    } finally {
      setLoading(false);
    }
  };

  return { dispatch, loading };
};
